import gql from 'graphql-tag';

import {useArtifactOverviewQuery} from '../../../generated/graphql';
import {addSeconds, differenceInDays} from '../../../util/date';

export const ARTIFACT_OVERVIEW_QUERY = gql`
  query ArtifactOverview($artifactID: ID!, $artifactVersionIndex: String!) {
    artifact(id: $artifactID) {
      id
      artifactSequence {
        id
        name
        project {
          id
          name
          entityName
        }
        artifactMembership(aliasName: $artifactVersionIndex) {
          id
          aliases {
            id
            alias
          }
        }
      }
      digest
      description
      size
      fileCount
      createdAt
      createdBy {
        __typename
        ... on Run {
          id
          runName: name
          displayName
          runProjectQuery: project {
            id
            runProject: name
            runEntity: entityName
          }
        }
        ... on User {
          id
          username
        }
      }
      artifactCollections {
        edges {
          node {
            __typename
            id
            ... on ArtifactPortfolio {
              linkPortfolioName: name
              linkProjectQuery: project {
                id
                linkProjectName: name
                linkEntityName: entityName
              }
              artifactMemberships {
                edges {
                  node {
                    id
                    versionIndex
                  }
                }
              }
            }
          }
        }
      }
      usedCount
      ttlDurationSeconds
    }
  }
`;

export function useArtifactOverviewQueryCaller(
  artifactID: string,
  artifactVersionIndex: string
) {
  const {data, loading} = useArtifactOverviewQuery({
    variables: {
      artifactID,
      artifactVersionIndex,
    },
    skip: artifactID === '' || artifactVersionIndex === '',
  });

  const artifactResults = data?.artifact;
  const artifactName = artifactResults?.artifactSequence?.name;
  const projectName = artifactResults?.artifactSequence?.project?.name;
  const entityName = artifactResults?.artifactSequence?.project?.entityName;

  const expiryTimestamp =
    artifactResults?.ttlDurationSeconds &&
    artifactResults?.ttlDurationSeconds > 0
      ? addSeconds(
          new Date(artifactResults?.createdAt),
          artifactResults?.ttlDurationSeconds
        )
      : null;

  const ttlDaysLeft = expiryTimestamp
    ? differenceInDays(expiryTimestamp, new Date())
    : null;

  const collectionAliases =
    artifactResults?.artifactSequence?.artifactMembership?.aliases ?? [];

  const artifact = {
    name: artifactName,
    projectName: projectName,
    entityName: entityName,
    ttlDaysLeft: ttlDaysLeft,
    createdBy: artifactResults?.createdBy,
    artifactCollections: artifactResults?.artifactCollections,
    aliases: collectionAliases,
    digest: artifactResults?.digest,
    createdAt: artifactResults?.createdAt,
    description: artifactResults?.description,
    usedCount: artifactResults?.usedCount,
    fileCount: artifactResults?.fileCount,
    size: artifactResults?.size,
  };

  return {
    artifact,
    loading,
  };
}
